import React, { FC } from 'react';
import cn from 'clsx';
import { Banner as BannerType, SearchAlertsResult } from '@v2/shared/lib/types';
import { useCart } from '@v2/shared/lib/cart';

function canViewBanners(result: SearchAlertsResult): boolean {
  return !result.r;
}

type Props = {
  banner: BannerType;
  wrapperClassName?: string;
  imageClassName?: string;
  doNotFallbackToMobile?: boolean;
  children?: React.ReactNode;
};

const Wrapper: FC<Props> = ({ banner, wrapperClassName, children }) => {
  return (
    <div className={wrapperClassName} style={banner.bg_color ? { backgroundColor: banner.bg_color } : {}}>
      {children}
    </div>
  );
};

const Banner: FC<Props> = ({ banner, wrapperClassName, imageClassName, doNotFallbackToMobile }) => {
  const { cart, loaded } = useCart();

  // A restricted banner stays hidden until the cart has been read, then appears if the
  // visitor is allowed it. Erring the other way would show it and snatch it back, and
  // once the server renders this markup for everyone that gap stops being theoretical.
  //
  // Having read the cart is the condition, not having an `sa` — that lives on the cart
  // object, so a visitor without one never gets it, and waiting for it would hide these
  // banners from most people permanently. No cart means no restriction, which is also
  // what the old endpoint fell back to when its request failed.
  if (banner.sa && (!loaded || !canViewBanners(cart?.sa ?? { r: false }))) {
    return <Wrapper banner={banner} wrapperClassName={wrapperClassName} />;
  }

  const mobileImage = banner.mobile_image ? (
    <img
      alt={banner.name}
      src={banner.mobile_image.url}
      width={banner.mobile_image.width}
      height={banner.mobile_image.height}
      className={cn(imageClassName, 'md:hidden')}
    />
  ) : null;

  const desktopImage = (
    <img
      alt={banner.name}
      src={banner.image.url}
      width={banner.image.width}
      height={banner.image.height}
      className={cn(imageClassName, mobileImage || doNotFallbackToMobile ? 'block max-md:hidden' : '')}
    />
  );

  const bannerImages = (
    <Wrapper banner={banner} wrapperClassName={wrapperClassName}>
      {desktopImage}
      {mobileImage}
    </Wrapper>
  );

  if (banner.url) {
    const linkProps: React.AnchorHTMLAttributes<HTMLAnchorElement> = {
      href: banner.url,
    };

    if (banner.no_follow) {
      linkProps.rel = 'nofollow';
    }

    if (banner.open_in_new_tab) {
      linkProps.target = '_blank';
    }

    return <a {...linkProps}>{bannerImages}</a>;
  }

  return bannerImages;
};

export default Banner;
